import React from 'react';
import { Dialog } from '@/shared/components/Dialog';
import { Textarea } from '@/shared/components/TextField';
import { Snackbar } from '@/shared/components/Snackbar';
import FileUpload from '@/shared/components/FileUpload/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import {
  COMPLETE_SR_LABELS as C,
  UPDATE_SR_STATUS_LABELS as L,
} from '../../constants/labels';
import type { ServiceRequestStatus } from '../../types/serviceRequestListType';
import { STATUS_CONFIG } from './viewServiceRequestHelpers';
import { SRStatusBadge } from './viewServiceRequestUI';

/** File types an admin may attach when completing a request. */
const ALLOWED_FILE_TYPES = [
  FileType.JPG,
  FileType.JPEG,
  FileType.PNG,
  FileType.PDF,
  FileType.DOCX,
];

interface CompleteServiceRequestModalProps {
  open: boolean;
  onClose: () => void;
  currentStatus: ServiceRequestStatus;

  // File selection (uploaded to S3 only on submit)
  files: File[];
  onFilesChange: (files: File[]) => void;

  // Toast (file-validation / notes-or-attachment rule / upload failures)
  toastMessage: string;
  onToast: (message: string) => void;
  onClearToast: () => void;

  // Admin notes
  adminNotes: string;
  onAdminNotesChange: (value: string) => void;
  adminNotesError: string;

  // Submit
  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
}

const InfoIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#474A4C" strokeWidth="1.5" />
    <line
      x1="12"
      y1="11"
      x2="12"
      y2="16.5"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="12" cy="8" r="1" fill="#474A4C" />
  </svg>
);

/** A read-only status field box (label floated above a status badge). */
const StatusField: React.FC<{
  label: string;
  testId: string;
  children: React.ReactNode;
}> = ({ label, testId, children }) => (
  <div
    className="relative rounded-lg bg-[#E5E7E9] opacity-60 cursor-not-allowed px-3 w-[318px] max-w-full"
    style={{ minHeight: 56 }}
    data-testid={testId}
    aria-disabled="true"
  >
    <span
      className="absolute left-3 pointer-events-none"
      style={{
        top: 0,
        paddingTop: 8,
        fontSize: 12,
        lineHeight: '16px',
        color: '#6a7278',
      }}
    >
      {label}
    </span>
    <div className="pt-7 pb-2">{children}</div>
    <div
      className="absolute bottom-0"
      style={{ left: 6, right: 6, height: 1, backgroundColor: '#6a7278' }}
    />
  </div>
);

/** Status badge for an arbitrary status (used for the fixed "Completed" target). */
const StatusChip: React.FC<{ status: ServiceRequestStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className="inline-flex items-center gap-2 rounded-lg self-start"
      style={{
        backgroundColor: cfg?.bg ?? '#F3F4F6',
        color: cfg?.text ?? '#9CA3AF',
        paddingLeft: 8,
        paddingRight: 12,
        paddingTop: 4,
        paddingBottom: 4,
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: '0.1px',
        fontWeight: 500,
      }}
    >
      <span
        className="rounded-full shrink-0"
        style={{ width: 6, height: 6, backgroundColor: cfg?.text ?? '#9CA3AF' }}
      />
      {cfg?.label ?? status}
    </span>
  );
};

const CompleteServiceRequestModal: React.FC<
  CompleteServiceRequestModalProps
> = ({
  open,
  onClose,
  currentStatus,
  files,
  onFilesChange,
  toastMessage,
  onToast,
  onClearToast,
  adminNotes,
  onAdminNotesChange,
  adminNotesError,
  isSubmitting,
  submitError,
  onSubmit,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      data-testid="complete-sr-modal"
    >
      <div className="flex flex-col relative">
        <div className="px-6 pt-6 pb-2">
          <h2
            className="text-neutral-10 font-normal"
            style={{ fontSize: 22, lineHeight: '28px' }}
            data-testid="complete-sr-title"
          >
            {C.TITLE}
          </h2>
        </div>

        <div className="px-6 pt-4 flex flex-col gap-8 max-h-[60vh] overflow-y-auto">
          <StatusField
            label={L.FIELD_CURRENT_STATUS}
            testId="complete-sr-current-status-field"
          >
            <SRStatusBadge status={currentStatus} />
          </StatusField>

          <div className="flex flex-col gap-2">
            <StatusField
              label={L.FIELD_NEW_STATUS}
              testId="complete-sr-new-status-field"
            >
              <StatusChip status="completed" />
            </StatusField>

            <div
              className="flex items-start gap-2"
              data-testid="complete-sr-irreversible-banner"
            >
              <InfoIcon />
              <p style={{ fontSize: 14, lineHeight: '20px', color: '#474A4C' }}>
                <span className="font-semibold">
                  {L.BANNER_IRREVERSIBLE_BOLD}
                </span>
                {L.BANNER_IRREVERSIBLE_REST}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-sm leading-5 tracking-[0.25px] text-start text-[#474A4C] font-normal">
              {C.SECTION_UPLOAD}
            </p>
            <FileUpload
              onFileSelect={onFilesChange}
              selectedFiles={files}
              allowedTypes={ALLOWED_FILE_TYPES}
              maxFiles={C.MAX_FILES}
              maxFileSizeMb={C.MAX_FILE_SIZE_MB}
              onError={onToast}
              disabled={isSubmitting}
              hideMaxFilesMessage
              fullWidth
            />
          </div>

          <div data-field="complete-sr-admin-notes">
            <Textarea
              label={C.FIELD_ADMIN_NOTES}
              placeholder={C.PLACEHOLDER_ADMIN_NOTES}
              value={adminNotes}
              onChange={(e) =>
                onAdminNotesChange(
                  (e.target as HTMLTextAreaElement).value ?? ''
                )
              }
              maxLength={L.NOTES_MAX_LENGTH}
              rows={4}
              fullWidth
              error={Boolean(adminNotesError)}
              errorMessage={adminNotesError}
              disabled={isSubmitting}
              dataTestId="complete-sr-admin-notes-input"
            />
          </div>

          {submitError && (
            <p
              className="text-error-40"
              style={{ fontSize: 12, lineHeight: '16px' }}
              data-testid="complete-sr-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-6">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="complete-sr-cancel"
          >
            {C.BUTTON_CANCEL}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#1f3340] cursor-pointer'
            }`}
            data-testid="complete-sr-submit"
          >
            {isSubmitting ? C.BUTTON_SUBMITTING : C.BUTTON_SUBMIT}
          </button>
        </div>
      </div>

      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        variant="error"
        onClose={onClearToast}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
    </Dialog>
  );
};

export default CompleteServiceRequestModal;
